import { formatCurrency } from '@ahm/common-helpers';
import { toast } from '@ahm/react-ui';
import {
  Box,
  Button,
  Grid,
  Group,
  rem,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import dayjs, { locale } from 'dayjs';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import type { BestHourSuggest } from '../../api/service';
import { useBestHoursSuggestMutation } from '../../api/service/use-best-hour-suggest';
import ClockIcon from '@/assets/icons/clock-green.svg';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

locale('vi');
locale('en');

type BookingBestHourSuggestProps = {
  onSchedule: (orderTime: number) => void;
};

const BookingBestHourSuggest = ({
  onSchedule,
}: BookingBestHourSuggestProps) => {
  const tPickupTime = useTranslations('booking.pickup_time');
  const tCommon = useTranslations('common');

  const {
    selectedService,
    estimatedRequestData,
    orderTime,
    samedaySelectedTime,
    setSamedaySelectedTime,
    bestHourSuggest,
  } = useBookingStore((state) => state);

  const [listBestHours, setListBestHours] = useState<BestHourSuggest[] | null>(
    null
  );
  const [tempSelectedSlot, setTempSelectedSlot] =
    useState<BestHourSuggest | null>(null);

  const { mutate: suggestBestHoursMutation, isPending: isLoading } =
    useBestHoursSuggestMutation({
      onSuccess: (data) => {
        setListBestHours(data);
      },
      onError: (error) => {
        console.error('Error fetching best hours:', error);
        toast.error({
          title: error.message || 'Error',
          description: 'Failed to fetch best hours. Please try again.',
        });
      },
    });

  useEffect(() => {
    if (isLoading) return;

    if (bestHourSuggest?.length) {
      setListBestHours(bestHourSuggest);
      return;
    }

    if (
      estimatedRequestData?.path &&
      selectedService?.service_hours &&
      selectedService._id
    ) {
      const body = {
        path: estimatedRequestData?.path || [],
        service_id: selectedService._id,
      };
      suggestBestHoursMutation(body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatedRequestData?.path, selectedService?._id]);

  // Initialize selected slot from existing orderTime or samedaySelectedTime
  useEffect(() => {
    if (!listBestHours) return;

    // Priority 1: Use samedaySelectedTime if available
    if (samedaySelectedTime?.est_time) {
      const matchingSlot = listBestHours.find(
        (slot) => slot.est_time === samedaySelectedTime.est_time
      );
      if (matchingSlot) {
        setTempSelectedSlot(matchingSlot);
        return;
      }
    }

    // Priority 2: Use orderTime if available
    if (orderTime) {
      const matchingSlot = listBestHours.find(
        (slot) => slot.est_time === orderTime
      );
      if (matchingSlot) {
        setTempSelectedSlot(matchingSlot);
        return;
      }
    }

    // Priority 3: Auto-select first slot if no match found
    if (listBestHours.length > 0 && !tempSelectedSlot) {
      setTempSelectedSlot(listBestHours[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listBestHours, orderTime, samedaySelectedTime]);

  const handleConfirmSchedule = () => {
    if (tempSelectedSlot) {
      onSchedule(tempSelectedSlot.est_time);
      setSamedaySelectedTime(tempSelectedSlot);
    }
  };

  const formatTimeSlots = (time: BestHourSuggest['start_time']) => {
    return dayjs().startOf('day').add(time, 'minute').format('HH:mm');
  };

  const formatTimeRange = (slot: BestHourSuggest) => {
    const {
      start_time: start,
      end_time: end,
      est_date_time: estDateTime,
    } = slot;
    const isToday = dayjs().isSame(dayjs(estDateTime), 'day');
    const dayLabel = isToday
      ? tPickupTime('today')
      : dayjs(estDateTime).locale('vi').format('dddd');

    return `${dayLabel}, ${formatTimeSlots(start)} - ${formatTimeSlots(end)}`;
  };

  if (isLoading) {
    return (
      <Stack gap={rem(8)} p={rem(16)} h={rem(300)}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} h={32} w={`${100 - index * 35}%`} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack>
      <ScrollArea h={rem(272)} scrollbars="y">
        <Stack gap={rem(8)} p={rem(16)}>
          {listBestHours?.map((slot) => {
            const activeSlot = slot.est_time === tempSelectedSlot?.est_time;
            return (
              <Group
                key={slot.est_time}
                // h={rem(48)}
                p={rem(12)}
                justify="space-between"
                align="flex-start"
                w="100%"
                style={{
                  cursor: 'pointer',
                  borderRadius: rem(8),
                  border: activeSlot
                    ? `1px solid var(--mantine-color-primary-6)`
                    : 'none',
                  backgroundColor: activeSlot
                    ? 'var(--mantine-color-primary-1)'
                    : 'transparent',
                  transition: 'background-color 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  if (!activeSlot) {
                    e.currentTarget.style.backgroundColor =
                      'var(--mantine-color-neutral-1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeSlot) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                onClick={() => {
                  setTempSelectedSlot(slot);
                }}
              >
                <Stack gap={0}>
                  <Text
                    size="md"
                    fw={activeSlot ? 600 : 500}
                    c={activeSlot ? 'primary.6' : 'neutral.7'}
                    truncate
                    style={{
                      textTransform: 'capitalize',
                    }}
                  >
                    {formatTimeRange(slot)}
                  </Text>

                  {slot?.surcharge_fee ? (
                    <Text size="xs" fw={500} c="neutral.6">
                      Phụ phí cuối tuần +{formatCurrency(slot.surcharge_fee)}
                    </Text>
                  ) : null}
                </Stack>

                {slot?.sla_time ? (
                  <Group
                    gap={rem(4)}
                    align="center"
                    w="no-wrap"
                    p={rem(2)}
                    style={{
                      borderRadius: rem(4),
                      backgroundColor: 'var(--mantine-color-green-1)',
                    }}
                  >
                    <ClockIcon width={20} height={20} />
                    <Text size="sm" fw={500} c="green.6">
                      Giao trước{' '}
                      <Box component="span" fw={600}>
                        {formatTimeSlots(slot.sla_time)}
                      </Box>
                    </Text>
                  </Group>
                ) : null}
              </Group>
            );
          })}
        </Stack>
      </ScrollArea>

      <Box p="md" w="100%">
        <Button
          size="md"
          mih={48}
          fullWidth
          fw={700}
          disabled={!tempSelectedSlot}
          onClick={handleConfirmSchedule}
        >
          {tCommon('continues')}
        </Button>
      </Box>
    </Stack>
  );
};

export default BookingBestHourSuggest;
